import React, { useState, useEffect, useRef } from 'react';
import { OverlayTrigger, Tooltip, Overlay, Popover, Modal, Button } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useEditor, EditorContent } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';

// 1. Define a global extension that allows attributes for ALL tags
const GlobalHtmlAttributes = Extension.create({
  name: 'globalHtmlAttributes',
  addGlobalAttributes() {
    return [
      {
        // 'nodes' applies these attributes to EVERY block and inline element (p, h1, li, etc.)
        types: 'nodes',
        attributes: {
          id: {
            default: null,
            parseHTML: (element) => element.getAttribute('id'),
            renderHTML: (attributes) => (attributes.id ? { id: attributes.id } : {}),
          },
          class: {
            default: null,
            parseHTML: (element) => element.getAttribute('class'),
            renderHTML: (attributes) => (attributes.class ? { class: attributes.class } : {}),
          },
          style: {
            default: null,
            parseHTML: (element) => element.getAttribute('style'),
            renderHTML: (attributes) => (attributes.style ? { style: attributes.style } : {}),
          },
        },
      },
    ];
  },
});

function FormRichEditor({
  label = '',
  name,
  value = '',
  onChange = () => {},
  disabled = false,
  validation = { valid: true },
  meta = { validate: { rules: '' }, acl: { read: true, write: true } },
  infoText = '',
  autogrow = true,
}) {
  const { messages } = useIntl();
  const appIntl = useSelector((state) => state.intl);

  const [focused, setFocused] = useState(false);
  const [showHtmlModal, setShowHtmlModal] = useState(false);
  const [htmlDraft, setHtmlDraft] = useState('');

  const mainRef = useRef(null);
  const timeoutRef = useRef(null);

  const aclWrite = meta?.acl?.write ?? true;
  const isRequired = meta?.validate?.rules?.includes('required') ?? false;
  const checkDisabled = () => disabled || !aclWrite;

  const latestProps = useRef({ name, onChange });
  useEffect(() => {
    latestProps.current = { name, onChange };
  });

  // Initialize Tiptap Editor with new extensions
  const editor = useEditor({
    extensions: [
      StarterKit,
      GlobalHtmlAttributes, // 3. Add global attribute permission
      Underline,
      TextAlign.configure({
        // Defines which HTML elements the alignment may be applied to
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        // Prevents links from opening directly when clicked in editor mode.
        openOnClick: false,
        autolink: true,
      }),
    ],
    content: value,
    editable: !checkDisabled(),
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const cleanHtml = html === '<p></p>' ? '' : html;

      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        latestProps.current.onChange(latestProps.current.name, cleanHtml);
      }, 120);
    },
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
  });

  // Safe synchronization of the external value prop
  useEffect(() => {
    // Only overwrite the editor's content if the value actually changed from the outside
    // AND the user is NOT currently focusing/typing inside the editor.
    if (editor && value !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) {
      editor.setOptions({ editable: !checkDisabled() });
    }
  }, [disabled, aclWrite, editor]);

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const openHtmlModal = () => {
    if (editor) {
      setHtmlDraft(editor.getHTML());
      setShowHtmlModal(true);
    }
  };

  const saveHtmlModal = () => {
    if (editor) {
      editor.commands.setContent(htmlDraft, true);
    }
    setShowHtmlModal(false);
    onChange(name, htmlDraft);
  };

  // Helper function for setting/removing links via prompt
  const toggleLink = () => {
    if (!editor) return;

    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }

    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL eingeben:', previousUrl);

    if (url === null) return; // Cancelled
    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div
      ref={mainRef}
      className={`form-group form-group-default ${focused ? 'focused ' : ''}${isRequired ? 'required ' : ''}${!validation.valid ? 'has-error ' : ''}${checkDisabled() ? 'disabled ' : ''}`}
      style={{ overflow: 'visible' }}
    >
      <label onClick={() => editor?.commands.focus()}>
        {label}
        {!!infoText && (
          <span className="m-l-5">
            <OverlayTrigger placement="top" overlay={<Tooltip>{infoText}</Tooltip>}>
              <i className="far fa-question-circle cursor-help" />
            </OverlayTrigger>
          </span>
        )}
      </label>

      <div className="text-right m-b-5">
        {!checkDisabled() && (
          <Button variant="secondary" size="sm" onClick={openHtmlModal}>
            &lt;/&gt; HTML
          </Button>
        )}
      </div>

      {/* Extended Toolbar */}
      {editor && !checkDisabled() && (
        <div
          className="tiptap-toolbar m-b-5"
          style={{
            display: 'flex',
            gap: '4px',
            flexWrap: 'wrap',
            background: '#f5f5f5',
            padding: '5px',
            border: '1px solid #ccc',
            borderBottom: 'none',
          }}
        >
          {(() => {
            const btnStyle = {
              width: '26px',
              height: '26px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0',
            };

            return (
              <>
                {/* Formatting */}
                <button
                  type="button"
                  className={`btn btn-xs ${editor.isActive('bold') ? 'btn-primary' : 'btn-default'}`}
                  style={btnStyle}
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  title="Fett / Bold"
                  aria-label="Fett / Bold"
                >
                  <i className="fas fa-bold" />
                </button>
                <button
                  type="button"
                  className={`btn btn-xs ${editor.isActive('italic') ? 'btn-primary' : 'btn-default'}`}
                  style={btnStyle}
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  title="Kursiv / Italic"
                  aria-label="Kursiv / Italic"
                >
                  <i className="fas fa-italic" />
                </button>
                <button
                  type="button"
                  className={`btn btn-xs ${editor.isActive('underline') ? 'btn-primary' : 'btn-default'}`}
                  style={btnStyle}
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  title="Unterstrichen / Underline"
                  aria-label="Unterstrichen / Underline"
                >
                  <i className="fas fa-underline" />
                </button>

                <span style={{ borderLeft: '1px solid #ccc', margin: '0 3px' }} />

                {/* Headings */}
                <button
                  type="button"
                  className={`btn btn-xs ${editor.isActive('heading', { level: 1 }) ? 'btn-primary' : 'btn-default'}`}
                  style={btnStyle}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                  title="Überschrift 1 / Heading 1"
                  aria-label="Überschrift 1 / Heading 1"
                >
                  <strong style={{ fontSize: '11px' }}>H1</strong>
                </button>
                <button
                  type="button"
                  className={`btn btn-xs ${editor.isActive('heading', { level: 2 }) ? 'btn-primary' : 'btn-default'}`}
                  style={btnStyle}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  title="Überschrift 2 / Heading 2"
                  aria-label="Überschrift 2 / Heading 2"
                >
                  <strong style={{ fontSize: '11px' }}>H2</strong>
                </button>
                <button
                  type="button"
                  className={`btn btn-xs ${editor.isActive('heading', { level: 3 }) ? 'btn-primary' : 'btn-default'}`}
                  style={btnStyle}
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  title="Überschrift 3 / Heading 3"
                  aria-label="Überschrift 3 / Heading 3"
                >
                  <strong style={{ fontSize: '11px' }}>H3</strong>
                </button>

                <span style={{ borderLeft: '1px solid #ccc', margin: '0 3px' }} />

                {/* Lists */}
                <button
                  type="button"
                  className={`btn btn-xs ${editor.isActive('bulletList') ? 'btn-primary' : 'btn-default'}`}
                  style={btnStyle}
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  title="Aufzählung / Bullet list"
                  aria-label="Aufzählung / Bullet list"
                >
                  <i className="fas fa-list-ul" />
                </button>
                <button
                  type="button"
                  className={`btn btn-xs ${editor.isActive('orderedList') ? 'btn-primary' : 'btn-default'}`}
                  style={btnStyle}
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  title="Nummerierte Liste / Ordered list"
                  aria-label="Nummerierte Liste / Ordered list"
                >
                  <i className="fas fa-list-ol" />
                </button>

                <span style={{ borderLeft: '1px solid #ccc', margin: '0 3px' }} />

                {/* Alignment */}
                <button
                  type="button"
                  className={`btn btn-xs ${editor.isActive({ textAlign: 'left' }) ? 'btn-primary' : 'btn-default'}`}
                  style={btnStyle}
                  onClick={() => editor.chain().focus().setTextAlign('left').run()}
                  title="Linksbündig / Align left"
                  aria-label="Linksbündig / Align left"
                >
                  <i className="fas fa-align-left" />
                </button>
                <button
                  type="button"
                  className={`btn btn-xs ${editor.isActive({ textAlign: 'center' }) ? 'btn-primary' : 'btn-default'}`}
                  style={btnStyle}
                  onClick={() => editor.chain().focus().setTextAlign('center').run()}
                  title="Zentriert / Center"
                  aria-label="Zentriert / Center"
                >
                  <i className="fas fa-align-center" />
                </button>
                <button
                  type="button"
                  className={`btn btn-xs ${editor.isActive({ textAlign: 'right' }) ? 'btn-primary' : 'btn-default'}`}
                  style={btnStyle}
                  onClick={() => editor.chain().focus().setTextAlign('right').run()}
                  title="Rechtsbündig / Align right"
                  aria-label="Rechtsbündig / Align right"
                >
                  <i className="fas fa-align-right" />
                </button>

                <span style={{ borderLeft: '1px solid #ccc', margin: '0 3px' }} />

                {/* Link */}
                <button
                  type="button"
                  className={`btn btn-xs ${editor.isActive('link') ? 'btn-primary' : 'btn-default'}`}
                  style={btnStyle}
                  onClick={toggleLink}
                  title="Link einfügen oder entfernen / Insert or remove link"
                  aria-label="Link einfügen oder entfernen / Insert or remove link"
                >
                  <i className="fas fa-link" />
                </button>
              </>
            );
          })()}
        </div>
      )}

      {/* Editor Input Area */}
      <div
        className="tiptap-editor-wrapper"
        style={{
          minHeight: autogrow ? '100px' : '250px',
          height: 'auto',
          border: '1px solid #ccc',
          padding: '10px',
          background: checkDisabled() ? '#f0f0f0' : '#fff',
        }}
      >
        <EditorContent editor={editor} />
      </div>

      {!validation.valid && (
        <Overlay show={focused} target={mainRef.current} placement="top">
          <Popover className="popover-modal" id={`error_${name}_${label}`}>
            {validation.helpText}
          </Popover>
        </Overlay>
      )}

      {/* HTML Editing Modal */}
      <Modal show={showHtmlModal} onHide={() => setShowHtmlModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{messages?.form_rich_editor?.edit_html || 'HTML bearbeiten'}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <textarea
            className="form-control"
            rows={18}
            value={htmlDraft}
            onChange={(e) => setHtmlDraft(e.target.value)}
            spellCheck={false}
            style={{ fontFamily: 'monospace' }}
          />
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHtmlModal(false)}>
            {messages?.form_rich_editor?.cancel || 'Cancel'}
          </Button>
          <Button variant="primary" onClick={saveHtmlModal}>
            {messages?.form_rich_editor?.save || 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default FormRichEditor;
